import React, { useState, useRef, useEffect } from "react";
import { Form, Button, Spinner, Alert, ListGroup } from "react-bootstrap";
import * as familiaApi from "../../api/familia";
import * as monedasApi from "../../api/monedas";

/** Formulario para buscar (autocomplete) o crear una familia: mientras el usuario escribe se
 * sugieren familias existentes; si no encuentra la suya puede elegir "Crear nueva familia" desde
 * el propio combo. Al continuar: si eligió una familia existente, se muestra quién es el Jefe y se
 * le manda una solicitud por correo para que apruebe el ingreso; si es nueva, se pide el nombre y
 * se crea, quedando el usuario como Jefe. Se usa tanto en "Mi Familia" (siempre disponible) como en
 * la pantalla forzada de primer ingreso (con `accionExtra` para "no quiero pertenecer a ninguna").
 * `onResuelto` se llama al crear o al pedir unirse con éxito. */
export default function BuscarCrearFamiliaForm({ onResuelto, accionExtra }) {
  const [texto, setTexto] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [buscandoSugerencias, setBuscandoSugerencias] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [modoCrear, setModoCrear] = useState(false);
  const [nombreNuevaFamilia, setNombreNuevaFamilia] = useState("");
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [monedas, setMonedas] = useState([]);
  const [monedaId, setMonedaId] = useState("");
  const timerRef = useRef(null);

  // Monedas para elegir la de la nueva familia; viene preseleccionada la predeterminada del sistema
  // (para alguien que todavía no tiene familia, "predeterminada" es la global).
  useEffect(() => {
    monedasApi.listarMonedas()
      .then((lista) => {
        const activas = lista.filter((m) => m.activo);
        setMonedas(activas);
        setMonedaId(String((activas.find((m) => m.predeterminada) ?? activas[0])?.monedaId ?? ""));
      })
      .catch(() => { /* sin lista, la familia se crea con la moneda por defecto del sistema */ });
  }, []);

  const handleChangeTexto = (e) => {
    const valor = e.target.value;
    setTexto(valor);
    setMostrarDropdown(true);
    clearTimeout(timerRef.current);
    if (valor.trim().length < 2) {
      setSugerencias([]);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setBuscandoSugerencias(true);
      try {
        setSugerencias(await familiaApi.listarSugerencias(valor.trim()));
      } catch {
        setSugerencias([]);
      } finally {
        setBuscandoSugerencias(false);
      }
    }, 300);
  };

  const handleSeleccionarSugerencia = (nombre) => {
    setTexto(nombre);
    setMostrarDropdown(false);
    setSugerencias([]);
  };

  const handleElegirCrearNueva = () => {
    setNombreNuevaFamilia(texto.trim());
    setModoCrear(true);
    setMostrarDropdown(false);
  };

  const handleContinuar = async () => {
    setError("");
    const nombre = texto.trim();
    if (!nombre) {
      setError("Escribe el nombre de tu familia.");
      return;
    }
    setMostrarDropdown(false);
    setCargando(true);
    try {
      const r = await familiaApi.buscarPorNombre(nombre);
      if (r.existe) {
        setResultado(r);
      } else {
        setNombreNuevaFamilia(nombre);
        setModoCrear(true);
      }
    } catch (err) {
      setError(err?.response?.data?.mensaje || "No se pudo buscar la familia.");
    } finally {
      setCargando(false);
    }
  };

  const handleCrear = async () => {
    setError("");
    const nombre = nombreNuevaFamilia.trim();
    if (!nombre) {
      setError("Escribe el nombre de la nueva familia.");
      return;
    }
    setGuardando(true);
    try {
      await familiaApi.crearFamilia(nombre, monedaId ? Number(monedaId) : null);
      await onResuelto();
    } catch (err) {
      setError(err?.response?.data?.mensaje || "No se pudo crear la familia.");
      setGuardando(false);
    }
  };

  const handleSolicitar = async () => {
    setError("");
    setGuardando(true);
    try {
      await familiaApi.solicitarUnirse(resultado.nombre);
      await onResuelto();
    } catch (err) {
      setError(err?.response?.data?.mensaje || "No se pudo enviar la solicitud.");
      setGuardando(false);
    }
  };

  const volver = () => {
    setResultado(null);
    setModoCrear(false);
    setError("");
  };

  if (resultado) {
    return (
      <div>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        <p>
          La familia <strong>{resultado.nombre}</strong> ya existe, liderada por{" "}
          <strong>{resultado.nombreJefe}</strong>. Le enviaremos un correo para que apruebe tu ingreso.
        </p>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={volver} disabled={guardando}>Cancelar</Button>
          <Button variant="primary" onClick={handleSolicitar} disabled={guardando}>
            {guardando ? <Spinner size="sm" animation="border" /> : "Enviar solicitud"}
          </Button>
        </div>
      </div>
    );
  }

  if (modoCrear) {
    return (
      <div>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        <p className="text-muted">No existe ninguna familia con ese nombre. Escribe cómo se va a llamar -- quedarás como su Jefe.</p>
        <Form.Group className="mb-3">
          <Form.Label>Nombre de la nueva familia</Form.Label>
          <Form.Control
            value={nombreNuevaFamilia}
            onChange={(e) => setNombreNuevaFamilia(e.target.value)}
            autoFocus
            required
          />
        </Form.Group>
        {monedas.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>Moneda de la familia</Form.Label>
            <Form.Select value={monedaId} onChange={(e) => setMonedaId(e.target.value)} style={{ maxWidth: 320 }}>
              {monedas.map((m) => (
                <option key={m.monedaId} value={m.monedaId}>{m.codigo} — {m.nombre} ({m.simbolo})</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">Todos los miembros verán sus montos en esta moneda. Si te equivocas, el administrador puede cambiarla.</Form.Text>
          </Form.Group>
        )}
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={volver} disabled={guardando}>Cancelar</Button>
          <Button variant="primary" onClick={handleCrear} disabled={guardando}>
            {guardando ? <Spinner size="sm" animation="border" /> : "Crear familia"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted">Escribe el nombre de tu familia. Si ya existe, le pedimos al Jefe que te acepte; si no existe, la creas y quedas de Jefe.</p>
      {error && <Alert variant="danger" className="py-2">{error}</Alert>}

      <div className="position-relative">
        <div className="d-flex gap-2">
          <Form.Control
            placeholder="Nombre de tu familia"
            value={texto}
            onChange={handleChangeTexto}
            onFocus={() => setMostrarDropdown(true)}
            onBlur={() => setTimeout(() => setMostrarDropdown(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleContinuar();
              }
            }}
            autoComplete="off"
          />
          <Button variant="primary" onClick={handleContinuar} disabled={cargando || !texto.trim()}>
            {cargando ? <Spinner size="sm" animation="border" /> : "Continuar"}
          </Button>
        </div>

        {mostrarDropdown && texto.trim().length >= 2 && (
          <ListGroup className="position-absolute w-100 shadow-sm" style={{ zIndex: 10, top: "100%" }}>
            {buscandoSugerencias && (
              <ListGroup.Item className="text-muted small">
                <Spinner size="sm" animation="border" className="me-2" />Buscando...
              </ListGroup.Item>
            )}
            {!buscandoSugerencias && sugerencias.map((f) => (
              <ListGroup.Item
                key={f.familiaId}
                action
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSeleccionarSugerencia(f.nombre)}
              >
                {f.nombre}
              </ListGroup.Item>
            ))}
            {!buscandoSugerencias && (
              <ListGroup.Item
                action
                className="text-primary"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleElegirCrearNueva}
              >
                <i className="bi bi-plus-circle me-2"></i>
                {sugerencias.length === 0
                  ? `No se encontró ninguna familia. Crear nueva familia "${texto.trim()}"`
                  : "¿No la encuentras? Crear nueva familia"}
              </ListGroup.Item>
            )}
          </ListGroup>
        )}
      </div>

      {accionExtra}
    </div>
  );
}
